import { useState, useEffect, useRef, useCallback } from "react";
import { useLocation } from "react-router-dom";

interface DropdownItem {
  label: string;
  href: string;
}

const cityItems: DropdownItem[] = [
  { label: "台北", href: "/locations/taipei" },
  { label: "台北中山區", href: "/locations/taipei-zhongshan" },
  { label: "台北大安區", href: "/locations/taipei-daan" },
  { label: "台北信義區", href: "/locations/taipei-xinyi" },
  { label: "新北", href: "/locations/newtaipei" },
  { label: "台中", href: "/locations/taichung" },
  { label: "高雄", href: "/locations/kaohsiung" },
  { label: "台南", href: "/locations/tainan" },
];

const topicItems: DropdownItem[] = [
  { label: "放鬆療癒", href: "/topics/wellness-relax" },
  { label: "美容保養", href: "/topics/beauty-care" },
  { label: "外貌美化", href: "/topics/appearance" },
  { label: "身心靈", href: "/topics/mind-body-spirit" },
  { label: "光療舒壓", href: "/topics/mind-body-relief" },
  { label: "健康養生", href: "/topics/health-wellness" },
  { label: "形象與攝影", href: "/topics/image-photo" },
  { label: "彩妝造型", href: "/topics/makeup-styling" },
  { label: "暖罐", href: "/topics/warm-cupping" },
  { label: "色彩鑑定", href: "/topics/color-analysis" },
  { label: "穿搭", href: "/topics/fashion-styling" },
];

const aboutItems: DropdownItem[] = [
  { label: "關於我們", href: "/about" },
  { label: "常見問題", href: "/faq" },
  { label: "加入我們", href: "/join-us" },
  { label: "合作夥伴", href: "/partners" },
  { label: "隱私政策", href: "/privacy" },
  { label: "使用條款", href: "/terms" },
];

const newsItems: DropdownItem[] = [
  { label: "淡江市集 5/18-5/24", href: "/events/danshui-market-2026" },
  { label: "政大辦桌彩妝小站 5/24", href: "/events/nccu-makeup-station-2026" },
];

export default function Navbar() {
  const [scrolled, setScrolled] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [activeDropdown, setActiveDropdown] = useState<string | null>(null);
  const location = useLocation();
  const isHome = location.pathname === "/";
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const navRef = useRef<HTMLElement>(null);

  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 60);
    };
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  useEffect(() => {
    setActiveDropdown(null);
    setMobileMenuOpen(false);
  }, [location.pathname]);

  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (navRef.current && !navRef.current.contains(e.target as Node)) {
        setActiveDropdown(null);
        setMobileMenuOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  // Keyboard: Escape closes dropdowns
  useEffect(() => {
    const handleKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") {
        setActiveDropdown(null);
      }
    };
    document.addEventListener("keydown", handleKey);
    return () => document.removeEventListener("keydown", handleKey);
  }, []);

  const openDropdown = useCallback((key: string) => {
    if (timerRef.current) clearTimeout(timerRef.current);
    setActiveDropdown(key);
  }, []);

  const closeDropdown = useCallback(() => {
    timerRef.current = setTimeout(() => {
      setActiveDropdown(null);
    }, 150);
  }, []);

  const cancelClose = useCallback(() => {
    if (timerRef.current) clearTimeout(timerRef.current);
  }, []);

  const isLightMode = !isHome || scrolled;

  const navLinkBase = `relative text-[15px] font-medium transition-colors duration-200 hover:text-primary cursor-pointer whitespace-nowrap flex items-center gap-1 py-2 ${isLightMode ? "text-slate-700" : "text-white/90"}`;

  const isDropdownActive = (key: string) => activeDropdown === key;

  return (
    <nav
      ref={navRef}
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        isLightMode
          ? "bg-white/95 backdrop-blur-md shadow-sm"
          : "bg-transparent"
      }`}
    >
      <div className="w-full px-4 md:px-8 lg:px-14">
        <div className="flex items-center justify-between h-16 md:h-[70px]">
          {/* Logo */}
          <a href="/" className="flex items-center gap-2.5 shrink-0">
            <div className="w-9 h-9 rounded-xl overflow-hidden shrink-0">
              <img
                src="https://readdy.ai/api/search-image?query=A%20bold%20geometric%20compass%20needle%20logo%20icon%20for%20a%20beauty%20discovery%20guide%20brand%2C%20featuring%20a%20sharp%20symmetrical%20directional%20pointer%20pointing%20northeast%20inside%20a%20perfect%20circular%20ring%2C%20with%20a%20small%20decorative%20star%20or%20diamond%20shape%20at%20the%20center%20pivot%20point%2C%20rendered%20in%20a%20striking%20two-tone%20palette%20of%20vivid%20burnt%20orange%20and%20warm%20ivory%20white%2C%20ultra-clean%20flat%20vector%20graphic%20with%20thick%20confident%20lines%2C%20absolutely%20no%20clock%20elements%2C%20no%20text%2C%20centered%20on%20a%20solid%20pure%20white%20background%2C%20modern%20app%20icon%20style%2C%20crisp%20edges%20and%20minimalist%20design%20suitable%20for%20website%20navigation%20bar%20branding&width=512&height=512&seq=gtg-compass-v3&orientation=squarish"
                alt="美好時光指南 Logo"
                className="w-full h-full object-cover"
              />
            </div>
            <span
              className={`font-bold text-base md:text-lg tracking-tight transition-colors ${
                isLightMode ? "text-slate-800" : "text-white"
              }`}
            >
              美好時光指南
            </span>
          </a>

          {/* Desktop Nav - Centered */}
          <div className="hidden md:flex items-center gap-1">
            {/* 最新消息 Dropdown */}
            <div
              className="relative"
              onMouseEnter={() => openDropdown("news")}
              onMouseLeave={closeDropdown}
              onFocus={() => openDropdown("news")}
              onBlur={closeDropdown}
            >
              <a
                href="/events/danshui-market-2026"
                className={navLinkBase}
                aria-expanded={isDropdownActive("news")}
                aria-haspopup="menu"
                aria-controls="nav-news-menu"
              >
                最新消息
                <i
                  className={`ri-arrow-down-s-line text-sm transition-transform duration-200 ${
                    isDropdownActive("news") ? "rotate-180" : ""
                  } ${isLightMode ? "text-slate-400" : "text-white/60"}`}
                />
              </a>
              {isDropdownActive("news") && (
                <div
                  className="absolute top-full left-1/2 -translate-x-1/2 mt-1.5 bg-white border border-slate-100 rounded-xl py-2 w-[240px] z-50 shadow-lg shadow-slate-200/40"
                  id="nav-news-menu"
                  role="menu"
                  onMouseEnter={cancelClose}
                  onMouseLeave={closeDropdown}
                >
                  <div className="px-3 py-2">
                    <span className="text-[11px] font-semibold text-slate-400 uppercase tracking-wider">
                      近期活動
                    </span>
                  </div>
                  <div className="px-1.5">
                    {newsItems.map((item) => (
                      <a
                        key={item.href}
                        href={item.href}
                        role="menuitem"
                        className="flex items-center px-3 py-2 rounded-lg hover:bg-slate-50 transition-colors group"
                      >
                        <span className="text-sm text-slate-700 group-hover:text-primary transition-colors">
                          {item.label}
                        </span>
                      </a>
                    ))}
                  </div>
                </div>
              )}
            </div>

            {/* 探索城市 Dropdown */}
            <div
              className="relative"
              onMouseEnter={() => openDropdown("cities")}
              onMouseLeave={closeDropdown}
              onFocus={() => openDropdown("cities")}
              onBlur={closeDropdown}
            >
              <a
                href="/locations"
                className={navLinkBase}
                aria-expanded={isDropdownActive("cities")}
                aria-haspopup="menu"
                aria-controls="nav-cities-menu"
              >
                探索城市
                <i
                  className={`ri-arrow-down-s-line text-sm transition-transform duration-200 ${
                    isDropdownActive("cities") ? "rotate-180" : ""
                  } ${isLightMode ? "text-slate-400" : "text-white/60"}`}
                />
              </a>
              {isDropdownActive("cities") && (
                <div
                  className="absolute top-full left-1/2 -translate-x-1/2 mt-1.5 bg-white border border-slate-100 rounded-xl py-2 w-[210px] z-50 shadow-lg shadow-slate-200/40"
                  id="nav-cities-menu"
                  role="menu"
                  onMouseEnter={cancelClose}
                  onMouseLeave={closeDropdown}
                >
                  <div className="px-3 py-2">
                    <span className="text-[11px] font-semibold text-slate-400 uppercase tracking-wider">
                      熱門城市
                    </span>
                  </div>
                  <div className="px-1.5">
                    {cityItems.map((item) => (
                      <a
                        key={item.href}
                        href={item.href}
                        role="menuitem"
                        className="flex items-center px-3 py-2 rounded-lg hover:bg-slate-50 transition-colors group"
                      >
                        <span className="text-sm text-slate-700 group-hover:text-primary transition-colors">
                          {item.label}
                        </span>
                      </a>
                    ))}
                  </div>
                  <div className="border-t border-slate-50 mt-1 pt-1.5 px-3">
                    <a
                      href="/locations"
                      className="flex items-center gap-1 text-xs font-medium text-primary hover:text-primary/80 transition-colors"
                    >
                      查看全部
                      <i className="ri-arrow-right-line" />
                    </a>
                  </div>
                </div>
              )}
            </div>

            {/* 美業探索 Dropdown */}
            <div
              className="relative"
              onMouseEnter={() => openDropdown("topics")}
              onMouseLeave={closeDropdown}
              onFocus={() => openDropdown("topics")}
              onBlur={closeDropdown}
            >
              <a
                href="/topics"
                className={navLinkBase}
                aria-expanded={isDropdownActive("topics")}
                aria-haspopup="menu"
                aria-controls="nav-topics-menu"
              >
                美業探索
                <i
                  className={`ri-arrow-down-s-line text-sm transition-transform duration-200 ${
                    isDropdownActive("topics") ? "rotate-180" : ""
                  } ${isLightMode ? "text-slate-400" : "text-white/60"}`}
                />
              </a>
              {isDropdownActive("topics") && (
                <div
                  className="absolute top-full left-1/2 -translate-x-1/2 mt-1.5 bg-white border border-slate-100 rounded-xl py-2 w-[210px] z-50 shadow-lg shadow-slate-200/40"
                  id="nav-topics-menu"
                  role="menu"
                  onMouseEnter={cancelClose}
                  onMouseLeave={closeDropdown}
                >
                  <div className="px-3 py-2">
                    <span className="text-[11px] font-semibold text-slate-400 uppercase tracking-wider">
                      美業主題
                    </span>
                  </div>
                  <div className="px-1.5">
                    {topicItems.map((item) => (
                      <a
                        key={item.href}
                        href={item.href}
                        role="menuitem"
                        className="flex items-center px-3 py-2 rounded-lg hover:bg-slate-50 transition-colors group"
                      >
                        <span className="text-sm text-slate-700 group-hover:text-primary transition-colors">
                          {item.label}
                        </span>
                      </a>
                    ))}
                  </div>
                  <div className="border-t border-slate-50 mt-1 pt-1.5 px-3">
                    <a
                      href="/topics"
                      className="flex items-center gap-1 text-xs font-medium text-primary hover:text-primary/80 transition-colors"
                    >
                      查看全部
                      <i className="ri-arrow-right-line" />
                    </a>
                  </div>
                </div>
              )}
            </div>

            {/* 關於我們 Dropdown */}
            <div
              className="relative"
              onMouseEnter={() => openDropdown("about")}
              onMouseLeave={closeDropdown}
              onFocus={() => openDropdown("about")}
              onBlur={closeDropdown}
            >
              <a
                href="/about"
                className={navLinkBase}
                aria-expanded={isDropdownActive("about")}
                aria-haspopup="menu"
                aria-controls="nav-about-menu"
              >
                關於我們
                <i
                  className={`ri-arrow-down-s-line text-sm transition-transform duration-200 ${
                    isDropdownActive("about") ? "rotate-180" : ""
                  } ${isLightMode ? "text-slate-400" : "text-white/60"}`}
                />
              </a>
              {isDropdownActive("about") && (
                <div
                  className="absolute top-full left-1/2 -translate-x-1/2 mt-1.5 bg-white border border-slate-100 rounded-xl py-2 w-[210px] z-50 shadow-lg shadow-slate-200/40"
                  id="nav-about-menu"
                  role="menu"
                  onMouseEnter={cancelClose}
                  onMouseLeave={closeDropdown}
                >
                  <div className="px-3 py-2">
                    <span className="text-[11px] font-semibold text-slate-400 uppercase tracking-wider">
                      站內資訊
                    </span>
                  </div>
                  <div className="px-1.5">
                    {aboutItems.map((item) => (
                      <a
                        key={item.href}
                        href={item.href}
                        role="menuitem"
                        className="flex items-center px-3 py-2 rounded-lg hover:bg-slate-50 transition-colors group"
                      >
                        <span className="text-sm text-slate-700 group-hover:text-primary transition-colors">
                          {item.label}
                        </span>
                      </a>
                    ))}
                  </div>
                </div>
              )}
            </div>

            {/* 探店精選 Simple Link */}
            <a href="/stores" className={navLinkBase}>
              探店精選
            </a>
          </div>

          {/* Right Actions */}
          <div className="hidden md:flex items-center gap-2">
            <a
              href="/search"
              className={`w-9 h-9 flex items-center justify-center rounded-full transition-colors ${
                isLightMode
                  ? "hover:bg-slate-100 text-slate-500"
                  : "hover:bg-white/20 text-white/80"
              }`}
            >
              <i className="ri-search-line text-lg" />
            </a>
            <span
              className="px-5 py-2 text-sm font-medium rounded-full bg-slate-100 text-slate-400 whitespace-nowrap cursor-default"
              title="會員系統即將上線"
            >
              登入 / 註冊
            </span>
          </div>

          {/* Mobile Hamburger */}
          <button
            className="md:hidden w-9 h-9 flex items-center justify-center cursor-pointer"
            onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
          >
            <i
              className={`ri-menu-line text-xl ${
                isLightMode ? "text-slate-800" : "text-white"
              }`}
            />
          </button>
        </div>
      </div>

      {/* Mobile Menu */}
      {mobileMenuOpen && (
        <div className="md:hidden bg-white border-t border-slate-100 shadow-lg max-h-[80vh] overflow-y-auto">
          <div className="px-4 py-4 space-y-1">
            {/* Mobile News Section */}
            <div className="space-y-1 pb-2">
              <p className="text-xs font-semibold text-slate-400 uppercase tracking-wider px-0 py-1">
                最新消息
              </p>
              {newsItems.map((item) => (
                <a
                  key={item.href}
                  href={item.href}
                  className="block text-sm font-medium text-slate-700 py-2 hover:text-primary"
                >
                  {item.label}
                </a>
              ))}
            </div>

            <a
              href="/locations"
              className="block text-sm font-medium text-slate-700 py-2.5 hover:text-primary"
            >
              探索城市
            </a>
            <a
              href="/topics"
              className="block text-sm font-medium text-slate-700 py-2.5 hover:text-primary"
            >
              美業探索
            </a>
            <a
              href="/stores"
              className="block text-sm font-medium text-slate-700 py-2.5 hover:text-primary"
            >
              探店精選
            </a>

            {/* Mobile About Section */}
            <div className="border-t border-slate-100 pt-3 mt-2 space-y-1">
              <p className="text-xs font-semibold text-slate-400 uppercase tracking-wider px-0 py-1">
                關於我們
              </p>
              {aboutItems.map((item) => (
                <a
                  key={item.href}
                  href={item.href}
                  className="block text-sm text-slate-600 py-1.5 hover:text-primary"
                >
                  {item.label}
                </a>
              ))}
            </div>

            <div className="border-t border-slate-100 pt-3 mt-2 space-y-1">
              <p className="text-xs font-semibold text-slate-400 uppercase tracking-wider px-0 py-1">
                熱門主題
              </p>
              {topicItems.slice(0, 6).map((item) => (
                <a
                  key={item.href}
                  href={item.href}
                  className="block text-sm text-slate-600 py-1.5 hover:text-primary"
                >
                  {item.label}
                </a>
              ))}
            </div>
            <div className="border-t border-slate-100 pt-3 mt-2 space-y-1">
              <a
                href="/search"
                className="flex items-center gap-2 text-sm font-medium text-slate-700 py-2 hover:text-primary"
              >
                <div className="w-4 h-4 flex items-center justify-center">
                  <i className="ri-search-line" />
                </div>
                搜尋
              </a>
              <span className="block text-sm font-medium text-slate-400 py-2 cursor-default">
                登入 / 註冊（即將上線）
              </span>
            </div>
          </div>
        </div>
      )}
    </nav>
  );
}